import {
  BadRequestException,
  Injectable,
  InternalServerErrorException,
  Logger,
} from '@nestjs/common';
import * as crypto from 'crypto';
import Razorpay from 'razorpay';
import { PrismaService } from '@cart/api-data-access';
import { CheckoutService } from '../checkout/checkout.service';
import { StoreContextService } from '../tenancy/store-context.service';
import { OrderSummary } from '@cart/shared-types';

export interface RazorpayOrderResponse {
  razorpayOrderId: string;
  amount: number;
  currency: string;
  keyId: string;
}

export interface VerifyPaymentInput {
  checkoutToken: string;
  razorpayOrderId: string;
  razorpayPaymentId: string;
  razorpaySignature: string;
}

@Injectable()
export class RazorpayService {
  private readonly logger = new Logger(RazorpayService.name);
  // Undefined (not constructed) when credentials are absent — payments are
  // deferred post-launch (see plan.md), so the app must boot cleanly without
  // them, not crash on every startup for an integration nothing calls yet.
  private readonly rzp?: Razorpay;
  private readonly keyId: string;
  private readonly keySecret: string;

  constructor(
    private readonly prisma: PrismaService,
    private readonly checkoutService: CheckoutService,
    private readonly storeContext: StoreContextService,
  ) {
    this.keyId     = process.env['RAZORPAY_KEY_ID']     ?? '';
    this.keySecret = process.env['RAZORPAY_KEY_SECRET'] ?? '';

    if (!this.keyId || !this.keySecret) {
      this.logger.warn('RAZORPAY_KEY_ID or RAZORPAY_KEY_SECRET not set — payment calls will fail');
      return;
    }

    this.rzp = new Razorpay({ key_id: this.keyId, key_secret: this.keySecret });
  }

  async createOrder(userId: string, checkoutToken: string): Promise<RazorpayOrderResponse> {
    if (!this.rzp) {
      throw new InternalServerErrorException('Payment gateway not configured');
    }

    const session = await this.checkoutService.findSession(checkoutToken);

    if (session.status !== 'open') {
      throw new BadRequestException('Checkout session is not open');
    }

    // grandTotal is stored in paise (smallest unit) already when currency is INR
    // If stored in rupees * 100 (paise), pass as-is; Razorpay expects paise
    const amount = session.grandTotal;
    const currency = session.currency ?? 'INR';

    let rzpOrder: { id: string };
    try {
      rzpOrder = await this.rzp.orders.create({
        amount,
        currency,
        receipt: checkoutToken,
        notes: { checkoutToken, userId, storeId: this.storeId() },
      }) as { id: string };
    } catch (err) {
      this.logger.error('Razorpay order creation failed', err);
      throw new InternalServerErrorException('Payment gateway error — please try again');
    }

    // Persist the razorpay order id on the checkout session for webhook reconciliation
    await this.prisma.checkoutSession.updateMany({
      where: { token: checkoutToken, storeId: this.storeId() },
      data: { providerOrderId: rzpOrder.id },
    });

    return { razorpayOrderId: rzpOrder.id, amount, currency, keyId: this.keyId };
  }

  async verifyAndComplete(userId: string, input: VerifyPaymentInput): Promise<OrderSummary> {
    this.assertSignature(input.razorpayOrderId, input.razorpayPaymentId, input.razorpaySignature);

    return this.checkoutService.completeSession(input.checkoutToken, userId, {
      paymentProvider:  'razorpay',
      paymentReference: input.razorpayPaymentId,
      paymentStatus:    'paid',
    });
  }

  async handleWebhook(rawBody: Buffer, signature: string): Promise<{ ok: boolean }> {
    const expectedSig = crypto
      .createHmac('sha256', this.keySecret)
      .update(rawBody)
      .digest('hex');

    if (!crypto.timingSafeEqual(Buffer.from(expectedSig), Buffer.from(signature))) {
      throw new BadRequestException('Invalid webhook signature');
    }

    let event: { event: string; payload: Record<string, unknown> };
    try {
      event = JSON.parse(rawBody.toString());
    } catch {
      throw new BadRequestException('Invalid webhook payload');
    }

    this.logger.log(`Razorpay webhook: ${event.event}`);

    switch (event.event) {
      case 'payment.captured':
        await this.handlePaymentCaptured(event.payload);
        break;
      case 'payment.failed':
        await this.handlePaymentFailed(event.payload);
        break;
      case 'refund.created':
        this.logger.log('Refund created — handle in next phase');
        break;
      default:
        this.logger.log(`Unhandled webhook event: ${event.event}`);
    }

    return { ok: true };
  }

  private assertSignature(orderId: string, paymentId: string, signature: string): void {
    const body = `${orderId}|${paymentId}`;
    const expected = crypto
      .createHmac('sha256', this.keySecret)
      .update(body)
      .digest('hex');

    if (!crypto.timingSafeEqual(Buffer.from(expected), Buffer.from(signature))) {
      throw new BadRequestException('Payment signature verification failed');
    }
  }

  private async handlePaymentCaptured(payload: Record<string, unknown>): Promise<void> {
    const payment = (payload as { payment?: { entity?: { order_id?: string; id?: string } } })
      ?.payment?.entity;
    if (!payment?.order_id || !payment?.id) return;

    const session = await this.prisma.checkoutSession.findFirst({
      where: { providerOrderId: payment.order_id },
      select: { id: true, status: true },
    });

    if (!session || session.status !== 'open') return;

    this.logger.log(`Webhook: payment captured for order ${payment.order_id}`);
  }

  private async handlePaymentFailed(payload: Record<string, unknown>): Promise<void> {
    const payment = (payload as { payment?: { entity?: { order_id?: string; error_description?: string } } })
      ?.payment?.entity;
    if (!payment?.order_id) return;
    this.logger.warn(`Webhook: payment failed for order ${payment.order_id} — ${payment.error_description}`);
  }

  private storeId(): string {
    return this.storeContext.storeId();
  }
}
